// FinancialYear.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useDbContext } from "../../../context/DbContext";
import { fetchActiveFinancialYear, selectActiveFyid } from "../../../app/financialYearSlice";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import ActionToolbar from "../../NavBars/ActionToolbar";
import FinancialYearChange from "./FinancialYearChange";
import { FiPlusCircle, FiEdit2, FiLogOut, FiSearch, FiXCircle } from "react-icons/fi";

function FinancialYear({ onClose, zIndex }) {
  const dispatch = useDispatch();
  const activeFyid = useSelector(selectActiveFyid);
  const { dbCode } = useDbContext();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const [showChangeForm, setShowChangeForm] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formMode, setFormMode] = useState("new");

  const fetchFinancialYears = async () => {
    if (!dbCode) return;
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/financialyear/getFinancialYears/${dbCode}`);
      if (response.data.success) {
        setFinancialYears(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching financial years:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinancialYears();
    if (dbCode) {
      dispatch(fetchActiveFinancialYear(dbCode));
    }
  }, [dbCode, dispatch]);

  // Search filter matching ID, dates, status, or narration
  const filteredData = financialYears.filter((row) => {
    const term = searchText.toLowerCase();
    return (
      row.ID?.toString().toLowerCase().includes(term) ||
      row.FromDate?.toLowerCase().includes(term) ||
      row.ToDate?.toLowerCase().includes(term) ||
      row.Status?.toLowerCase().includes(term) ||
      (row.Narration && row.Narration.toLowerCase().includes(term))
    );
  });

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto font-inter"
      style={{ zIndex: zIndex || 100 }}
    >
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col absolute overflow-hidden rounded-[10px]">
        {/* Top Title Bar */}
        <WindowHeader
          title="Financial Year"
          logo={headeLogo}
          onClose={onClose}
          className="!bg-[#213575]"
          closeBtnClassName="w-6 h-6 border-none rounded-[1px] mr-2"
        />

        <div className="w-full flex-1 flex flex-col p-4 bg-[#f8fafc] gap-4 box-border min-h-0">
          <div className="w-full bg-white rounded-lg border border-slate-200 shadow-sm flex flex-col flex-1 overflow-hidden">

            {/* Action Bar */}
            <div className=" border-b border-slate-200 flex flex-col gap-3">
              <ActionToolbar
                width="100%"
                bgColor="white"
                showSave={false}
                showPrint={false}
                showClear={false}
                showFind={false}
                showEdit={false}
                showDelete={false}
                showExit={false}
                showNavigation={false}
                className="border-b border-slate-200 p-1.5 box-border"
                buttonGap="6px"
                customButtons={[
                  {
                    text: "New Fiscal",
                    icon: <FiPlusCircle size={16} />,
                    onClick: () => {
                      setFormMode("new");
                      setSelectedYear(null);
                      setShowChangeForm(true);
                    },
                    className: "!bg-[#2a4392] whitespace-nowrap !text-white hover:!bg-[#1e3a8a] !border-none !rounded-[6px] !w-[75px] !h-[45px] !py-0.5 !px-2",
                    iconColor: "white",
                  },
                  {
                    text: "Edit",
                    icon: <FiEdit2 size={16} />,
                    onClick: () => {
                      setFormMode("edit");
                      const activeYear = financialYears.find(y => y.Status === 'Active');
                      if (activeYear) {
                        setSelectedYear(activeYear);
                        setShowChangeForm(true);
                      } else {
                        alert("No active financial year found to edit.");
                      }
                    },
                    className: "!bg-white !text-[#334155] hover:!bg-slate-50 !border !border-slate-300 !rounded-[6px] !w-[60px] !h-[45px] !py-0.5 !px-2 cursor-pointer",
                    iconColor: "#334155",
                  },
                  {
                    text: "Exit",
                    icon: <FiLogOut size={16} />,
                    onClick: onClose,
                    className: "!bg-white !text-red-500 hover:!bg-red-50 !border !border-red-300 !rounded-[6px] !w-[60px] !h-[45px] !py-0.5 !px-2",
                    iconColor: "#ef4444",
                  }
                ]}
              />

              {/* Search Bar */}
              <div className="flex items-center gap-2 mt-2 p-2">
                <div className="relative w-[250px] h-[34px]">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiSearch className="text-slate-400" size={14} />
                  </div>
                  <input
                    type="text"
                    placeholder="Enter text to search..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    className="block w-full h-full pl-9 pr-3 text-[13px] border border-slate-300 rounded-md bg-white focus:outline-none focus:border-[#2a4392] focus:ring-1 focus:ring-[#2a4392] box-border m-0"
                  />
                </div>
                <button 
                  className="bg-[#2a4392] hover:bg-[#1e3a8a] text-white px-5 h-[34px] rounded-md text-[13px] font-medium transition-colors border-none cursor-pointer flex items-center justify-center m-0"
                  onClick={fetchFinancialYears}
                >
                  Find
                </button>
                <button 
                  className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 px-5 h-[34px] rounded-md text-[13px] font-medium transition-colors cursor-pointer flex items-center justify-center m-0" 
                  onClick={() => setSearchText("")}
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 mt-4 overflow-auto bg-slate-50 relative flex flex-col">
              {loading ? (
                <div className="flex-1 flex items-center justify-center text-slate-500 text-[13px]">
                  Loading financial years...
                </div>
              ) : (
                <table className="w-full border-collapse text-[13px] text-left">
                  <thead className="bg-[#eef2f6] text-slate-500 sticky top-0 z-10">
                    <tr>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">ID</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">From Date</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">To Date</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">Status</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">Narration</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">Created User</th>
                      <th className="py-2 px-4 border-b border-slate-200 font-semibold uppercase text-[11px] tracking-wider">Created Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredData.map((row, index) => {
                      const isSelected = selectedYear?.ID === row.ID;
                      return (
                        <tr 
                          key={row.ID} 
                          onClick={() => setSelectedYear(row)}
                          className={`border-b border-slate-100 transition-colors cursor-pointer ${
                            isSelected
                              ? 'bg-blue-100 hover:bg-blue-150'
                              : index % 2 === 1
                                ? 'bg-[#eaf0fc] hover:bg-slate-100'
                                : 'bg-white hover:bg-slate-100'
                          }`}
                        >
                          <td className="py-3 px-4 text-slate-500">{row.ID}</td>
                          <td className="py-3 px-4 font-medium text-slate-800">{row.FromDate}</td>
                          <td className="py-3 px-4 font-medium text-slate-800">{row.ToDate}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <div className={`w-1 h-3 rounded-sm ${row.Status === 'Active' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <span className={`font-semibold ${row.Status === 'Active' ? 'text-green-600' : 'text-red-600'}`}>{row.Status}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-500">{row.Narration || "—"}</td>
                          <td className="py-3 px-4 font-medium text-slate-800">{row.CreatedUser || "—"}</td>
                          <td className="py-3 px-4 text-slate-500">{row.CreatedDate || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </div>
      </div>
      
      {showChangeForm && (
        <FinancialYearChange 
          onClose={() => setShowChangeForm(false)} 
          selectedYear={selectedYear}
          mode={formMode}
          onSaveSuccess={() => {
            setShowChangeForm(false);
            setSelectedYear(null);
            fetchFinancialYears();
          }}
          zIndex={(zIndex || 100) + 10} 
        />
      )}
    </div>
  );
}

export default FinancialYear;